import { ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@ui/lib/utils'
import { Icon } from '@ui/primitives/Icon'

const alertVariants = cva(
  'relative w-full rounded-lg border p-4',
  {
    variants: {
      variant: {
        default: 'bg-gray-50 border-gray-200 text-gray-900',
        info: 'bg-blue-50 border-blue-200 text-blue-900',
        success: 'bg-green-50 border-green-200 text-green-900',
        warning: 'bg-yellow-50 border-yellow-200 text-yellow-900',
        danger: 'bg-red-50 border-red-200 text-red-900',
        error: 'bg-red-50 border-red-200 text-red-900',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

const iconMap = {
  default: 'fa-solid fa-circle-info',
  info: 'fa-solid fa-circle-info',
  success: 'fa-solid fa-circle-check',
  warning: 'fa-solid fa-triangle-exclamation',
  danger: 'fa-solid fa-triangle-exclamation',
  error: 'fa-solid fa-circle-exclamation',
}

export interface AlertProps extends VariantProps<typeof alertVariants> {
  children: ReactNode
  className?: string
  icon?: string | boolean
  title?: string
  onClose?: () => void
}

export function Alert({
  children,
  className,
  variant,
  icon = true,
  title,
  onClose,
}: AlertProps) {
  const showIcon = icon !== false
  const iconName = typeof icon === 'string' ? icon : iconMap[variant || 'default']

  return (
    <div className={cn(alertVariants({ variant }), className)} role="alert">
      <div className="flex gap-3">
        {showIcon && (
          <div className="flex-shrink-0">
            <Icon name={iconName} size="sm" />
          </div>
        )}
        <div className="flex-1">
          {title && <div className="font-semibold mb-1">{title}</div>}
          <div className="text-sm">{children}</div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Close"
          >
            <Icon name="fa-solid fa-xmark" size="sm" />
          </button>
        )}
      </div>
    </div>
  )
}
